import React, { useState } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	makeStyles,
	createStyles,
} from "@material-ui/core";

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';

import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TableFooter from '@mui/material/TableFooter';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from "moment";
import { ukUA } from "@material-ui/core/locale";
import MomentUtils from "@date-io/moment";

import Certificate from "./Certificate";
import { CircularProgress } from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
	createStyles({
		paper: {
			width: "100%",
			maxWidth: 800,
		},
		openTable: {
			marginLeft: "10px",
		},
		headerDialog: {
			"& h2": {
				display: "flex",
				justifyContent: "space-between",
			},
		},
		circ: {
			marginLeft: "5px",
		},
	})
);

function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (event) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton
				onClick={handleFirstPageButtonClick}
				disabled={page === 0}
				aria-label="first page"
			>
				{theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton
				onClick={handleBackButtonClick}
				disabled={page === 0}
				aria-label="previous page"
			>
				{theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="next page"
			>
				{theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label="last page"
			>
				{theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

TablePaginationActions.propTypes = {
	count: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	rowsPerPage: PropTypes.number.isRequired,
};

const theme = createTheme(
	ukUA
);

export default function CertificationsDialog({ dialogProps, title, state }) {
	const [spreadsheetUrl, setSpreadsheetUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [valueFrom, setValueFrom] = useState(moment());
	const [valueTo, setValueTo] = useState(moment());
	const [dateFrom, setDateFrom] = useState(null);
	const [dateTo, setDateTo] = useState(null);
	const cls = useStyles();
	// keys = user emails
	const keys = Object.keys(state);
	const url = process.env.REACT_APP_FUNCTIONS_generateTableCertificates_URL;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeFrom = (newValue) => {
		setValueFrom(newValue);
		setDateFrom(newValue.unix());
	};

	const handleChangeTo = (newValue) => {
		setValueTo(newValue);
		setDateTo(newValue.unix());
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const generateTable = () => {
		setLoading(true);
		setSpreadsheetUrl(null);
		const params = dateFrom && dateTo ? { start: dateFrom, end: dateTo } : null;
		axios.get(url, { params }).then((res) => {
			if (res.status < 400 && res.data && res.data.status === "success") {
				setSpreadsheetUrl(res.data.spreadsheetUrl);
			}
		}).catch((err) => {
			console.error("Error while generating excel file: ", err.data);
		}).finally(() => {
			setLoading(false);
		});
	};

	return (
		<Dialog
			{...dialogProps}
			classes={{
				paper: cls.paper,
			}}>
			<DialogTitle className={cls.headerDialog}>
				Сертифікати
				<div class='certificate-list-header-block'>
					<DatePicker
						label="від"
						format="DD/MMM/YYYY"
						value={valueFrom}
						onChange={handleChangeFrom}
					/>
					<DatePicker
						label="до"
						format="DD/MMM/YYYY"
						value={valueTo}
						onChange={handleChangeTo}
					/>
					<Button
						variant="outlined"
						color="inherit"
						size="small"
						onClick={() => generateTable()}
						disabled={loading}>
						Згенерувати звіт
						{loading && <CircularProgress size={20} className={cls.circ} />}
					</Button>
					{spreadsheetUrl && (
						<a
							className={cls.openTable}
							target="_blank"
							rel="noopener noreferrer"
							href={spreadsheetUrl}>
							<Button variant="outlined" size="small" color="secondary">
								Відкрити таблицю
							</Button>
						</a>
					)}
				</div>
			</DialogTitle>
			<DialogContent dividers>
						<Table stickyHeader aria-label="sticky table">
							<TableBody>
								{keys.length
									? (rowsPerPage > 0
										? keys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										: keys).map((key, i) => (
										<TableRow key={key + i}>
											<TableCell>
												<Certificate
													key={key + i}
													email={key}
													renderData={state[key].renderData}
												/>
											</TableCell>
										</TableRow>
									))
									: "Сертифікатів не знайдено!"}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TablePagination
										rowsPerPageOptions={[5, 10, 25, 50, 100]}
										count={keys.length}
										rowsPerPage={rowsPerPage}
										page={page}
										SelectProps={{
											inputProps: {
												'aria-label': 'rows per page',
											},
											native: true,
										}}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										ActionsComponent={TablePaginationActions}
									/>
								</TableRow>
							</TableFooter>
						</Table>
			</DialogContent>
		</Dialog>
	);
}
